import { useExpandWidth } from "@redotech/react-animation/transition";
import { useHtmlEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import {
  useExpensiveLocationHover,
  useHover,
} from "@redotech/react-util/hover";
import { filterTruthy } from "@redotech/util/array";
import classNames from "classnames";
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useMatch } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { CommandPaletteOpenContext } from "../../command-palette/contexts/command-palette-context";
import { Flex } from "../../flex";
import { NavShowLeftPaddingContext } from "../../page";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { LinkPrimaryTab, RedoPrimaryNav } from "./primary/redo-primary-nav";
import * as navigationCss from "./redo-navigation.module.css";
import { RedoSettingsNav, SettingsSection } from "./redo-settings-nav";
import {
  RedoSecondaryNav,
  SecondaryNavigationData,
} from "./secondary/redo-secondary-nav";

/**
 * The id of the tab and the secondary navigation data must match.
 */
export interface PageNavigationEntry {
  primaryTab: LinkPrimaryTab;
  secondaryNavigationData: SecondaryNavigationData | undefined;
  /** Whether this product is actively enabled for the merchant. */
  isActiveProduct?: boolean;
}

export interface RedoSideNavigationProps {
  isHidden: boolean;
  enterSettingsUrl: string;
  exitSettingsUrl: string;
  settingsRoot: string;
  pageEntries: PageNavigationEntry[];
  settingSections: SettingsSection[] | null;
  hideTeamSwitcher?: boolean;
  /** Pass-through to RedoPrimaryNav. See its prop docs. */
  useLegacyPrimaryNavLayout?: boolean;
}

export const CollapseModeContext = createContext<{
  collapseMode: boolean;
  setCollapseMode: (collapseMode: boolean) => void;
}>({ collapseMode: false, setCollapseMode: (collapseMode: boolean) => {} });

export const RedoSideNavigation = memo(function RedoSideNavigation({
  isHidden,
  enterSettingsUrl,
  exitSettingsUrl,
  settingsRoot,
  pageEntries,
  settingSections,
  hideTeamSwitcher = false,
  useLegacyPrimaryNavLayout = false,
}: RedoSideNavigationProps) {
  const [navContainer, setNavContainer] = useState<HTMLDivElement | null>(null);

  const [collapseContainer, setCollapseContainer] =
    useState<HTMLSpanElement | null>(null);

  const [closingOnCollapseToggle, setClosingOnCollapseToggle] = useState(false);

  const [hasLeftSinceCollapse, setHasLeftSinceCollapse] = useState(true);

  const [collapseMode, setCollapseMode] = useLocalStorage(
    "redo_side_navigation_collapse_mode",
    false,
  );

  const handleSetCollapseMode = useHandler((collapseMode: boolean) => {
    if (collapseMode) {
      setClosingOnCollapseToggle(true);
      setHasLeftSinceCollapse(false);
    }
    setCollapseMode(collapseMode);
  });

  const navHovered = useExpensiveLocationHover(navContainer);

  const collapseContainerHovered = useHover(collapseContainer);

  const isHovered = navHovered || collapseContainerHovered;

  useEffect(() => {
    if (!isHovered && !hasLeftSinceCollapse) {
      setHasLeftSinceCollapse(true);
    }
  }, [isHovered, hasLeftSinceCollapse]);

  const location = useLocation();

  const isInSettings = useMatch(`${settingsRoot}/*`);

  useLayoutEffect(() => {
    if (isInSettings && collapseMode) {
      setCollapseMode(false);
    }
  }, [isInSettings, collapseMode, setCollapseMode]);

  const { setShowLeftPadding, setHasSecondaryNav } = useContext(
    NavShowLeftPaddingContext,
  );
  useLayoutEffect(() => {
    setShowLeftPadding(!!isInSettings);
  }, [isInSettings, setShowLeftPadding]);

  const [exitedToLeft, setExitedToLeft] = useState(false);

  const expanded =
    !isHidden &&
    !closingOnCollapseToggle &&
    (!collapseMode || (isHovered && hasLeftSinceCollapse) || exitedToLeft);

  const [animateStyles, animating] = useExpandWidth(
    collapseMode ? true : expanded,
    navContainer,
  );

  const [slideVisible, setSlideVisible] = useState(expanded);

  if (collapseMode && expanded && !slideVisible) {
    setSlideVisible(true);
  }

  const showContent = collapseMode
    ? expanded || slideVisible
    : animating || expanded;

  useHtmlEventListener(navContainer, "transitionend", (e) => {
    if (e.propertyName === "transform" && !expanded) {
      setSlideVisible(false);
    }
    if (
      (e.propertyName === "width" || e.propertyName === "transform") &&
      closingOnCollapseToggle
    ) {
      setClosingOnCollapseToggle(false);
    }
  });

  useHtmlEventListener(collapseContainer, "touchstart", () => {
    if (collapseMode) {
      setCollapseMode(false);
    }
  });

  useHtmlEventListener(document.documentElement, "mouseleave", (e) => {
    if (e.clientX < 10) {
      setExitedToLeft(true);
    }
  });

  useHtmlEventListener(document.documentElement, "mouseenter", () => {
    requestAnimationFrame(() => setExitedToLeft(false));
  });

  // Publish the right edge of the side nav as a CSS variable so floating
  // elements (e.g. feature-onboarding popups) can anchor to it across all
  // nav states: drag-resized primary nav, secondary nav open/closed, and
  // collapse mode (where the nav slides in/out via a CSS transform).
  // ResizeObserver doesn't fire during transform animations, so drive
  // updates with rAF while a transition is running.
  useEffect(() => {
    if (!navContainer) {
      return;
    }
    let rafId: number | null = null;
    const update = () => {
      const right = Math.max(0, navContainer.getBoundingClientRect().right);
      document.documentElement.style.setProperty(
        "--redo-side-nav-right",
        `${right}px`,
      );
    };
    const tick = () => {
      update();
      rafId = requestAnimationFrame(tick);
    };
    const stopTick = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
    const onTransitionRun = (e: TransitionEvent) => {
      if (e.propertyName === "transform" && rafId === null) {
        tick();
      }
    };
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "transform") {
        stopTick();
        update();
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(navContainer);
    window.addEventListener("resize", update);
    navContainer.addEventListener("transitionrun", onTransitionRun);
    navContainer.addEventListener("transitionend", onTransitionEnd);
    navContainer.addEventListener("transitioncancel", onTransitionEnd);
    return () => {
      stopTick();
      observer.disconnect();
      window.removeEventListener("resize", update);
      navContainer.removeEventListener("transitionrun", onTransitionRun);
      navContainer.removeEventListener("transitionend", onTransitionEnd);
      navContainer.removeEventListener("transitioncancel", onTransitionEnd);
    };
  }, [navContainer]);

  const pagePrimaryTabs = useMemo(
    () => pageEntries.map((entry) => entry.primaryTab),
    [pageEntries],
  );

  const primaryTabSections = useMemo(() => {
    const CORE_TAB_IDS = new Set(["home", "customer", "catalog", "orders"]);
    const core: LinkPrimaryTab[] = [];
    const activeProducts: LinkPrimaryTab[] = [];
    const inactiveProducts: LinkPrimaryTab[] = [];

    for (const entry of pageEntries) {
      if (CORE_TAB_IDS.has(entry.primaryTab.id)) {
        core.push(entry.primaryTab);
      } else if (entry.isActiveProduct === false) {
        inactiveProducts.push(entry.primaryTab);
      } else {
        activeProducts.push(entry.primaryTab);
      }
    }

    return { core, activeProducts, inactiveProducts };
  }, [pageEntries]);

  const selectedPrimaryTab = pagePrimaryTabs.find((tab) =>
    location.pathname.includes(tab.pathRoot),
  );

  // Split secondary navs into visible and invisible for global search
  const [visibleSecondaryNav, invisibleSecondaryNavs, allSecondaryNavEntries] =
    useMemo(() => {
      const allSecondaryNavEntries = filterTruthy(
        pageEntries.map((e) => e.secondaryNavigationData),
      );

      if (!selectedPrimaryTab?.id) {
        return [null, allSecondaryNavEntries, allSecondaryNavEntries];
      }
      const visible =
        allSecondaryNavEntries.find((e) => e.id === selectedPrimaryTab.id) ||
        null;
      const invisible = allSecondaryNavEntries.filter(
        (e) => e.id !== selectedPrimaryTab.id,
      );
      return [visible, invisible, allSecondaryNavEntries];
    }, [pageEntries, selectedPrimaryTab?.id]);

  const visiblePathRoot = pageEntries.find(
    ({ secondaryNavigationData }) =>
      secondaryNavigationData?.id === selectedPrimaryTab?.id,
  )?.primaryTab.pathRoot;

  useLayoutEffect(() => {
    const navActuallyVisible = !collapseMode || expanded;
    setHasSecondaryNav(
      (!!visibleSecondaryNav || !!isInSettings) && navActuallyVisible,
    );
  }, [
    visibleSecondaryNav,
    isInSettings,
    setHasSecondaryNav,
    collapseMode,
    expanded,
  ]);

  if (isInSettings) {
    return (
      <Flex grow={1} position="relative">
        <span
          className={navigationCss.collapseBar}
          ref={setCollapseContainer}
        />
        <Flex
          className={classNames(
            darkModeCss.darkMode,
            navigationCss.sideNav,
            collapseMode && navigationCss.collapseMode,
            collapseMode && !expanded && navigationCss.collapsed,
            expanded && navigationCss.expanding,
          )}
          dir="row"
          gap="none"
          grow={1}
          ref={setNavContainer}
          style={collapseMode ? undefined : animateStyles}
        >
          {showContent && settingSections && (
            <RedoSettingsNav
              exitSettingsUrl={exitSettingsUrl}
              sections={settingSections}
            />
          )}
        </Flex>
        {/* Render invisible main nav items for global search when in settings */}
        <InvisibleNavsForSearch
          invisibleSecondaryNavs={allSecondaryNavEntries}
        />
      </Flex>
    );
  }

  return (
    <Flex grow={1} position="relative">
      <span className={navigationCss.collapseBar} ref={setCollapseContainer} />
      <Flex
        className={classNames(
          darkModeCss.darkMode,
          navigationCss.sideNav,
          collapseMode && navigationCss.collapseMode,
          collapseMode && !expanded && navigationCss.collapsed,
          expanded && navigationCss.expanding,
        )}
        dir="row"
        gap="none"
        grow={1}
        ref={setNavContainer}
        style={collapseMode ? undefined : animateStyles}
      >
        {showContent && (
          <>
            <RedoPrimaryNav
              enterSettingsUrl={settingSections ? enterSettingsUrl : null}
              hideTeamSwitcher={hideTeamSwitcher}
              isCollapseMode={collapseMode}
              pageTabs={pagePrimaryTabs}
              primaryTabSections={primaryTabSections}
              selectedTabId={selectedPrimaryTab?.id}
              settingsRoot={settingsRoot}
              toggleCollapseMode={() => handleSetCollapseMode(!collapseMode)}
              useLegacyLayout={useLegacyPrimaryNavLayout}
            />
            {visibleSecondaryNav && visiblePathRoot && (
              <RedoSecondaryNav
                data={visibleSecondaryNav}
                pathRoot={visiblePathRoot}
              />
            )}
            <InvisibleNavsForSearch
              invisibleSecondaryNavs={invisibleSecondaryNavs}
            />
          </>
        )}
      </Flex>
      {/* Render invisible settings items for global search when not in settings */}
      {settingSections && <RedoSettingsNav hidden sections={settingSections} />}
    </Flex>
  );
});

/**
 * Only renders invisible secondary navs when the command palette is open.
 * This allows nav items to register with the tree context for global search
 * while keeping them out of the DOM during normal operation (preventing
 * Playwright from finding hidden elements).
 */
const InvisibleNavsForSearch = memo(function InvisibleNavsForSearch({
  invisibleSecondaryNavs,
}: {
  invisibleSecondaryNavs: SecondaryNavigationData[];
}) {
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  if (!isCommandPaletteOpen || invisibleSecondaryNavs.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "none" }}>
      {invisibleSecondaryNavs.map(({ NavContent, name }) => (
        <NavContent key={name} pathRoot="" />
      ))}
    </div>
  );
});
